import { useContext, type ReactNode } from 'react';
import { GlyphMark } from '../../assets/index.js';
import { PortalContext } from './PortalContext.js';
import styles from './AuthLayout.module.css';

export type AuthLayoutVariant = 'form-right' | 'form-left';
export type AuthLayoutPattern = 'none' | 'dots' | 'grid' | 'lines';

/**
 * Which portal is rendering this auth shell. Drives chrome differentiation
 * per the auth handoff (`for-engineering-auth-admin-consumer/README.md`
 * § "Portal differentiation"):
 * - `consumer` — warm editorial; `app.gc-coach.ai`. Default.
 * - `admin` — utilitarian/internal; `admin.gc-coach.ai`. Adds a full-width
 *   dark utility top bar, forces `pattern='grid'`, switches the brand panel
 *   to a cooler slate ground.
 *
 * Form-side widgets are identical between portals — the spec is explicit:
 * differentiation is chrome + copy only.
 */
export type AuthLayoutPortal = 'admin' | 'consumer';

export interface AuthLayoutProps {
  /**
   * Override the portal explicitly. Most callers should rely on
   * `<PortalProvider portal=...>` at the SPA root and omit this prop —
   * portal is a property of the SPA bundle, not the screen. The prop is
   * exposed for design-system isolation (Storybook, snapshots, design-
   * system docs) where context isn't set up.
   *
   * Resolution order: explicit prop > context > 'consumer' fallback.
   */
  portal?: AuthLayoutPortal;
  /**
   * Admin utility-bar content (left side). Typically "GC COACH · ADMIN
   * CONSOLE" in mono caps. Ignored when `portal='consumer'`.
   */
  utilityBarLeft?: ReactNode;
  /**
   * Admin utility-bar content (right side). Typically "INTERNAL ACCESS ·
   * admin.gc-coach.ai" in mono caps. Ignored when `portal='consumer'`.
   */
  utilityBarRight?: ReactNode;
  /** Form panel side. Build-time constant; not user-facing. Default 'form-right'. */
  variant?: AuthLayoutVariant;
  /**
   * Backdrop pattern on the brand panel. Default `'dots'` for consumer.
   * Forced to `'grid'` when `portal='admin'` per spec.
   */
  pattern?: AuthLayoutPattern;
  /** Brand + Form subtree (use <AuthLayout.Brand/> + <AuthLayout.Form/>). */
  children: ReactNode;
  /** Optional className passthrough on the root for app-specific overrides. */
  className?: string;
}

/**
 * Content slots for the brand panel. Per the design handoff `screen-strings.md`,
 * every string is owned by the consumer (admin app's i18n layer) so the
 * design-system stays free of locale state. Callers pass resolved strings in;
 * defaults below are illustrative EN values so the component is usable in
 * isolation (e.g. design-system snapshots).
 */
export interface AuthLayoutBrandProps {
  /**
   * The wordmark text. Default `"GC Coach"` (display-cased for consumer).
   * Admin passes `"GC COACH"` (mono-cased) per spec.
   */
  wordmark?: ReactNode;
  /**
   * Optional pill rendered next to the wordmark (e.g. an `"ADMIN"` badge for
   * the admin portal). When omitted, no badge renders.
   */
  wordmarkBadge?: ReactNode;
  /** "EST. MMXXVI" (consumer) or "INTERNAL BACK OFFICE" (admin) — small mono caption. */
  wordmarkSub?: ReactNode;
  /** Eyebrow above the headline. */
  eyebrow?: ReactNode;
  /** Main headline. */
  headline?: ReactNode;
  /** Tagline. */
  tagline?: ReactNode;
  /** Footer left. */
  footerLeft?: ReactNode;
  /** Footer right. */
  footerRight?: ReactNode;
  /**
   * Admin spec uses a mono-cased headline (to read "tool" not "product").
   * When `true`, the brand panel switches the headline font to mono.
   * Consumer/default `false`.
   */
  monoHeadline?: boolean;
}

export interface AuthLayoutFormProps {
  children: ReactNode;
}

/**
 * Two-panel auth shell: dark editorial brand panel (45%) + form panel (55%).
 * With `portal='admin'`, a full-width utility top bar renders above the two
 * panels; the brand panel switches to a cooler slate ground; and the pattern
 * is forced to `grid`.
 *
 * Compose as:
 * ```tsx
 * <AuthLayout portal="consumer" pattern="dots">
 *   <AuthLayout.Brand
 *     wordmarkSub="EST. MMXXVI"
 *     eyebrow="A MANAGEMENT TOOL · NOT LEGAL ADVICE"
 *     headline="The management coach for General Counsel."
 *     tagline="Decide, organize, prove value …"
 *     footerLeft="VERSION 0.5 · PILOT"
 *     footerRight="EU · FR / EN"
 *   />
 *   <AuthLayout.Form>
 *     <LangToggle ... />
 *     {screenContent}
 *   </AuthLayout.Form>
 * </AuthLayout>
 * ```
 *
 * For admin:
 * ```tsx
 * <AuthLayout
 *   portal="admin"
 *   utilityBarLeft="GC COACH · ADMIN CONSOLE"
 *   utilityBarRight="INTERNAL ACCESS · admin.gc-coach.ai"
 * >
 *   <AuthLayout.Brand
 *     wordmark="GC COACH"
 *     wordmarkBadge="ADMIN"
 *     wordmarkSub="INTERNAL BACK OFFICE"
 *     headline="Administration console."
 *     monoHeadline
 *     ...
 *   />
 *   <AuthLayout.Form>...</AuthLayout.Form>
 * </AuthLayout>
 * ```
 *
 * Below ~860px viewport width the brand panel is hidden and the form takes
 * the full width. The utility bar (admin) collapses with it.
 */
function AuthLayoutRoot({
  portal: propPortal,
  utilityBarLeft,
  utilityBarRight,
  variant = 'form-right',
  pattern = 'dots',
  children,
  className,
}: AuthLayoutProps) {
  // Resolution order: explicit prop > <PortalProvider> context > 'consumer'.
  const ctxPortal = useContext(PortalContext);
  const portal: AuthLayoutPortal = propPortal ?? ctxPortal ?? 'consumer';

  const effectivePattern: AuthLayoutPattern = portal === 'admin' ? 'grid' : pattern;
  const showUtilityBar =
    portal === 'admin' && (utilityBarLeft !== undefined || utilityBarRight !== undefined);

  const rootClasses = [
    styles.root,
    portal === 'admin' ? styles.portalAdmin : styles.portalConsumer,
    variant === 'form-left' ? styles.variantFormLeft : styles.variantFormRight,
    styles[`pattern_${effectivePattern}`],
    showUtilityBar ? styles.withUtilityBar : null,
    className,
  ]
    .filter(Boolean)
    .join(' ');

  return (
    <div className={rootClasses}>
      {showUtilityBar && (
        <div className={styles.utilityBar} aria-hidden="true">
          <span className={styles.utilityBarLeft}>
            <span className={styles.utilityBarSquare} aria-hidden="true" />
            {utilityBarLeft}
          </span>
          <span className={styles.utilityBarRight}>{utilityBarRight}</span>
        </div>
      )}
      <div className={styles.grid}>{children}</div>
    </div>
  );
}

function AuthLayoutBrand({
  wordmark = 'GC Coach',
  wordmarkBadge,
  wordmarkSub = 'EST. MMXXVI',
  eyebrow = 'A MANAGEMENT TOOL · NOT LEGAL ADVICE',
  headline = 'The management coach for General Counsel.',
  tagline = 'Decide, organize, prove value — without rendering legal advice. A management framework dedicated to GCs.',
  footerLeft = 'VERSION 0.5 · PILOT',
  footerRight = 'EU · FR / EN',
  monoHeadline = false,
}: AuthLayoutBrandProps) {
  return (
    <aside className={styles.brand} aria-hidden="true">
      <div className={styles.brandPattern} aria-hidden="true" />
      <div className={styles.brandInner}>
        {/* Top: glyph tile + wordmark lockup */}
        <div className={styles.brandLockup}>
          <span className={styles.brandMark}>
            <GlyphMark width={26} height={26} />
          </span>
          <span className={styles.brandWordmarkStack}>
            <span className={styles.brandWordmarkRow}>
              <span className={styles.brandWordmark}>{wordmark}</span>
              {wordmarkBadge && (
                <span className={styles.brandWordmarkBadge}>{wordmarkBadge}</span>
              )}
            </span>
            {wordmarkSub && (
              <span className={styles.brandWordmarkSub}>{wordmarkSub}</span>
            )}
          </span>
        </div>

        {/* Middle: eyebrow + headline + tagline */}
        <div className={styles.brandBody}>
          {eyebrow && <span className={styles.brandEyebrow}>{eyebrow}</span>}
          <h1
            className={`${styles.brandHeadline} ${monoHeadline ? styles.brandHeadlineMono : ''}`}
          >
            {headline}
          </h1>
          <p className={styles.brandTagline}>{tagline}</p>
        </div>

        {/* Bottom: version + region footer */}
        <div className={styles.brandFooter}>
          <span className={styles.brandFooterText}>{footerLeft}</span>
          <span className={styles.brandFooterText}>{footerRight}</span>
        </div>
      </div>
    </aside>
  );
}

function AuthLayoutForm({ children }: AuthLayoutFormProps) {
  return (
    <main className={styles.form}>
      <div className={styles.formInner}>{children}</div>
    </main>
  );
}

export const AuthLayout = Object.assign(AuthLayoutRoot, {
  Brand: AuthLayoutBrand,
  Form: AuthLayoutForm,
});
